import React, { useRef, useEffect, useState } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm         from '../../components/RSVPForm';
import Wishlist         from '../../components/Wishlist';
import Countdown        from '../../components/Countdown';
import Schedule         from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton   from '../../components/CalendarButton';
import FixedCTA         from '../../components/FixedCTA';
import YandexMap        from '../../components/YandexMap';
import useReveal        from '../../components/useReveal';
import BlockSubtitle    from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './BoldEditorial.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : (u.startsWith('http') ? u : u);

/* Convert "#RRGGBB" / "#RGB" / "rgb(...)" to "r, g, b" so CSS can plug it
   into rgba(var(--be-accent-rgb), α) in keyframes that need transparency. */
function accentToRgb(c) {
  if (!c) return '255, 0, 92';
  const s = String(c).trim();
  if (s.startsWith('#')) {
    const h = s.slice(1);
    const full = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    if (full.length >= 6) {
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      if (!Number.isNaN(r + g + b)) return `${r}, ${g}, ${b}`;
    }
  }
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return `${m[1]}, ${m[2]}, ${m[3]}`;
  return '255, 0, 92';
}

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

/* Load Unbounded (NauryzRedKeds fallback) + Montserrat once. */
function useBeFonts() {
  useEffect(() => {
    const id = 'be-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Unbounded:wght@700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* ── Polaroid stage scaler ────────────────────────────────────
   The stage has a fixed 358×248 internal design grid (absolute
   positioned children at pixel offsets from Figma). When the host
   container is narrower than 358px — e.g. the chatbot preview card
   (~260px) or the constructor phone frame on small screens — the
   children overflow. Wrap in an aspect-ratio box and apply
   transform:scale() so the whole stage shrinks proportionally. */
function PolaroidStageScaler({ children }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const update = () => {
      const stage = wrap.querySelector('.be-polaroid-stage');
      if (!stage) return;
      // Stage width is set by CSS (358 on mobile, 907.77 on desktop,
      // forced back to 358 inside preview sandboxes). Divide container
      // width by the CSS-declared design width to preserve aspect.
      const designW = parseFloat(getComputedStyle(stage).width) || 358;
      const wrapW = wrap.clientWidth;
      setScale(wrapW > 0 && designW > 0 ? Math.min(1, wrapW / designW) : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={wrapRef}
      className="be-polaroid-scaler"
      style={{ '--be-stage-scale': scale }}
    >
      {children}
    </div>
  );
}

/* ── Calendar widget matching Figma (binder clip, heart marker on "2") ─ */
function CalendarCard({ date, accent }) {
  if (!date) return null;
  const d = new Date(date);
  const yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7; // Mon-first
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++)    cells.push({ n: i, cur: true });
  while (cells.length < 42)         cells.push({ n: cells.length - off - dim + 1, cur: false });
  return (
    <div className="be-cal">
      <img className="be-cal-clip" src="/templates/bold-editorial/binder-clip.png" alt="" aria-hidden="true" />
      {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
        <span key={h} className="be-cal-head" style={{ color: accent }}>{h}</span>
      ))}
      {cells.map((c, i) => {
        const isToday = c.cur && c.n === day;
        return (
          <span key={i}
            className={`be-cal-cell${!c.cur ? ' be-cal-cell--muted' : ''}${isToday ? ' be-cal-cell--on' : ''}`}>
            {isToday && (
              <img className="be-cal-heart" src="/templates/bold-editorial/heart-marker.svg"
                   alt="" aria-hidden="true" />
            )}
            <span className="be-cal-num">{c.n}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ── Mobile nav w/ hamburger on small screens ─────────────────── */
function BeNav({ blocks, menu }) {
  const [open, setOpen] = useState(false);
  const links = [
    blocks.includes('schedule') && { href: '#be-schedule',  label: menu?.schedule || 'Программа' },
    blocks.includes('map')      && { href: '#be-location',  label: menu?.location || 'Локация'   },
    blocks.includes('palette')  && { href: '#be-dresscode', label: menu?.dresscode || 'Дресс-код' },
    blocks.includes('rsvp')     && { href: '#be-rsvp',      label: menu?.rsvp || 'Анкета'    },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <nav className="be-nav">
      {links.map(l => (
        <a key={l.href} href={l.href} onClick={() => setOpen(false)}>{l.label}</a>
      ))}
    </nav>
  );
}

/* ── Shared class maps ────────────────────────────────────────── */
const BE_SCHED_CLASSES = { root: 'be-sched', row: 'be-sched-row', time: 'be-sched-time', info: 'be-sched-info', name: 'be-sched-name', desc: 'be-sched-desc' };
const BE_CD_CLASSES    = { root: 'be-cd',    cell: 'be-cd-cell', num: 'be-cd-num', label: 'be-cd-lbl' };
const BE_DRESS_CLASSES = { wrap: 'be-swatches', swatchWrap: 'be-swatch-wrap', swatch: 'be-swatch', swatchLabel: 'be-swatch-lbl', photos: 'be-dc-photos', photo: 'be-dc-photo' };
const BE_RSVP_CLASSES  = {
  form:    'be-form',
  tabs:    'be-rsvp-tabs',
  tab:     'be-rsvp-tab',
  tabSel:  'be-rsvp-tab--sel',
  field:   'be-form-field',
  label:   'be-form-lbl',
  input:   'be-form-input',
  submit:  'be-btn be-btn--primary be-btn--full',
  success: 'be-form-ok',
  error:   'be-form-err',
  chip:    'be-chip',
  chipSel: 'be-chip--sel',
  chips:   'be-form-chips',
};
const BE_WL_CLASSES = {
  list:        'be-wish',
  item:        'be-wish-item',
  itemBorder:  '',
  row:         'be-wish-row',
  name:        'be-wish-name',
  price:       'be-wish-price',
  btn:         'be-btn be-btn--primary be-btn--full',
  btnReserved: 'be-btn--res',
};
const BE_WL_MODAL_CLASSES = {
  bg:         'be-wish-modal-bg',
  modal:      'be-wish-modal',
  title:      'be-wish-modal-title',
  subtitle:   'be-wish-modal-sub',
  input:      'be-form-input',
  error:      'be-form-err',
  btns:       'be-wish-modal-btns',
  btnCancel:  'be-btn be-btn--outline',
  btnConfirm: 'be-btn be-btn--primary',
};

/* ══ MAIN ══════════════════════════════════════════════════════ */
export default function BoldEditorial({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.be-reveal', inClass: 'be-in' });
  useBeFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};
  const bc = p.block_content || {};

  /* Colours */
  const accent = d.palette?.primary   || p.palette?.primary   || '#FF005C';
  const bg     = '#F0F3F6';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#1E1E1E';

  /* Blocks */
  const blocks = p.blocks || [];
  const HAS_RSVP     = blocks.includes('rsvp');

  /* Text */
  const bride    = p.bride  || 'Невеста';
  const groom    = p.groom  || 'Жених';
  const tagline  = bc.header?.subtitle || p.wedding_slogan || 'Не прошло и 5-ти лет';
  const invTitle = bc.message?.title  || 'Дорогие наши!';
  const invText  = p.invitation_text || bc.message?.text || '';
  const footerQuote = bc.footer?.quote || p.wedding_slogan || 'Одна любовь, другой такой и не было';

  /* Photos */
  const photos        = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto     = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;

  /* Polaroids show the 2nd and 3rd uploaded cover photos (indices 1 and
     2) — the first photo is reserved for the hero block above. Falls
     back to invitation_photos[] if the user uploaded a dedicated set. */
  const invPhotos = p.invitation_photos || [];
  const invP1 = invPhotos[0] || photos[1] || null;
  const invP2 = invPhotos[1] || photos[2] || null;

  const dcPhotos = p.dresscode_photos || [];

  /* Футер-фото независимо от главного: если не задано — футер без фото. */
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date / schedule / venues / wishlist / dresscode */
  const dateStr = fmtDateDots(p.wedding_date);
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = (p.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());
  const wishlist = p.wishlist || [];
  const swatches = p.dresscode_palette || [];

  /* Titles */
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`be-page${inPreview ? ' be-page--preview' : ''}`} ref={pageRef}
      style={{ '--be-accent': accent, '--be-accent-rgb': accentToRgb(accent), '--be-bg': bg, '--be-fg': fg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="be-hero be-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        <BeNav blocks={blocks} menu={bc.menu} />

        <div className="be-date-pill" style={{ background: accent }}>
          {dateStr}
        </div>

        <div className="be-hero-names">
          <span className="be-hero-name">{bride}</span>
          <span className="be-hero-amp">{bc.header?.amp || '&'}</span>
          <span className="be-hero-name">{groom}</span>
        </div>

        {tagline && <p className="be-hero-tagline">{tagline}</p>}

        {heroPhoto && (
          <div className="be-hero-photo">
            <div className="be-hero-photo-bg" />
            <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
          </div>
        )}
      </section>

      {/* ── INVITATION + POLAROIDS/CALENDAR ───────────────────── */}
      <section className="be-invite be-reveal">
        <div className="be-invite-left">
          <h2 className="be-h">{invTitle}</h2>
          <div className="be-invite-body">
            {invText
              ? invText.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
              : (
                <>
                  <p>С огромным волнением и радостью хотим пригласить вас</p>
                  <p>на ужин в честь нашего бракосочетания!</p>
                  <br />
                  <p>В этот особенный день мы будем счастливы видеть каждого</p>
                  <p>из вас, чтобы разделить волшебство момента и создать воспоминания, которые останутся в наших сердцах навсегда.</p>
                  <br />
                  <p>Пожалуйста, подтвердите своё присутствие через форму на нашем сайте или через любой мессенджер.</p>
                </>
              )
            }
          </div>
        </div>

        <div className="be-invite-right">
          <PolaroidStageScaler>
            <div className="be-polaroid-stage">
              {/* Polaroid A — rotated +9.65° (скрываем вместе со скотчем, если фото нет) */}
              {invP1 && (
                <>
                  <div className="be-polaroid be-polaroid--a">
                    <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
                  </div>
                  <img className="be-deco be-deco-tape" src="/templates/bold-editorial/tape.png" alt="" aria-hidden="true" />
                </>
              )}

              {/* Polaroid B — rotated -4.95° (скрываем вместе с клеем, если фото нет) */}
              {invP2 && (
                <>
                  <div className="be-polaroid be-polaroid--b">
                    <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
                  </div>
                  <img className="be-deco be-deco-glue" src="/templates/bold-editorial/glue.png" alt="" aria-hidden="true" />
                </>
              )}

              <CalendarCard date={p.wedding_date} accent={accent} />
            </div>
          </PolaroidStageScaler>

          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="be-btn be-btn--primary">
            {calBtn}
          </CalendarButton>
        </div>
      </section>

      {/* ── Dynamic blocks — Figma order ───────────────────────── */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="be-sect be-map-sect be-reveal" id="be-location">
                  <h2 className="be-h">{titleLocation}</h2>
                  <BlockSubtitle text={bc.map?.subtitle} className="be-p" />
                  {venues.map((v, i) => (
                    <div key={i} className="be-addr">
                      <img className="be-addr-icon" src="/templates/bold-editorial/location-pin.svg" alt="" aria-hidden="true" />
                      <span className="be-addr-text">
                        {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                      </span>
                    </div>
                  ))}
                  {!venues.length && p.wedding_address && (
                    <div className="be-addr">
                      <img className="be-addr-icon" src="/templates/bold-editorial/location-pin.svg" alt="" aria-hidden="true" />
                      <span className="be-addr-text">{p.wedding_address}</span>
                    </div>
                  )}
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="be-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#redIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule': {
              /* Пусто (удалили все пункты) → только заголовок и подзаголовок. */
              const items = schedule;
              return (
                <section key="schedule" className="be-sect be-sched-sect be-reveal" id="be-schedule">
                  <h2 className="be-h">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="be-p" />
                  <Schedule items={items} classes={BE_SCHED_CLASSES} />
                </section>
              );
            }

            case 'palette':
              return (
                <section key="palette" className="be-sect be-dress-sect be-reveal" id="be-dresscode">
                  <h2 className="be-h">{titleDress}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle} className="be-p" tag="div" />
                  <DresscodePalette
                    swatches={swatches}
                    photos={dcPhotos}
                    cssFilter={flt}
                    classes={BE_DRESS_CLASSES}
                  />
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="be-sect be-rsvp-sect be-reveal" id="be-rsvp">
                  <h2 className="be-h">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="be-p" />
                  <RSVPForm project={p} blocks={blocks} classes={BE_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="be-sect be-wish-sect be-reveal">
                  <h2 className="be-h">{titleWish}</h2>
                  <BlockSubtitle text={bc.wishlist?.subtitle} className="be-p" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={BE_WL_CLASSES}
                    modalClasses={BE_WL_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="be-sect be-cd-sect be-reveal">
                  <h2 className="be-h">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="be-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={BE_CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* ── FOOTER: photo + pink quote ──────────────────────────── */}
      <footer className="be-footer be-reveal">
        {footerPhoto && (
          <div className="be-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="be-footer-quote" style={{ background: accent }}>
          <p>{footerQuote}</p>
        </div>
      </footer>

      {/* ── Fixed CTA ──────────────────────────────────────────── */}
      {HAS_RSVP && (
        <FixedCTA
          href="#be-rsvp"
          targetId="be-rsvp"
          scrollRoot={pageRef}
          className="be-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
